import { Link, useLocation } from "react-router-dom";
import { marketingLocaleForPath } from "@/config/locales";
import { FEATURES } from "@/config/features";
import { SUPPORT_EMAIL } from "@/config/support";
import { Button } from "@/components/ui/button";
import { openConsentBanner } from "@/lib/consent";

export function PublicFooter() {
  const es = marketingLocaleForPath(useLocation().pathname) === "es";
  return (
    <footer className="border-t border-border bg-background py-10 md:py-12">
      <div className="container">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 md:gap-8">
          <div>
            <h3 className="text-sm font-heading font-semibold text-foreground mb-4">Product</h3>
            <ul className="space-y-2">
              {FEATURES.FREE_SCAN_ENABLED && (
                <li><Link to={es ? "/es/free-ai-visibility-scan" : "/scan"} className="text-sm text-muted-foreground hover:text-foreground transition-colors">{es ? "Auditoría gratuita" : "Free Audit"}</Link></li>
              )}
              
              
              <li><Link to="/ai-authority-report" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Monthly AI Authority Report</Link></li>
              <li><Link to="/ai-authority-index" className="text-sm text-muted-foreground hover:text-foreground transition-colors">AI Authority Index</Link></li>
              <li><Link to="/insights" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Insights</Link></li>
              <li><Link to={es ? "/es/pricing" : "/pricing"} className="text-sm text-muted-foreground hover:text-foreground transition-colors">{es ? "Precios" : "Pricing"}</Link></li>
              <li><Link to="/compare" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Compare</Link></li>
              <li><Link to="/methodology" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Methodology</Link></li>
              <li><Link to="/security" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Security</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="text-sm font-heading font-semibold text-foreground mb-4">Academy</h3>
            <ul className="space-y-2">
              <li><Link to="/academy" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Overview</Link></li>
              <li><Link to="/academy/getting-started" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Getting Started</Link></li>
              <li><Link to="/academy/methodology" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Methodology</Link></li>
              <li><Link to="/academy/guides" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Guides</Link></li>
              <li><Link to="/academy/glossary" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Glossary</Link></li>
              <li><Link to="/academy/faq" className="text-sm text-muted-foreground hover:text-foreground transition-colors">FAQ</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="text-sm font-heading font-semibold text-foreground mb-4">Company</h3>
            <ul className="space-y-2">
              <li><Link to="/company/about" className="text-sm text-muted-foreground hover:text-foreground transition-colors">About</Link></li>
              <li><Link to="/company/careers" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Careers</Link></li>
              <li><Link to="/partners" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Partners</Link></li>
              <li><Link to="/one-pager" className="text-sm text-muted-foreground hover:text-foreground transition-colors">One-Pager</Link></li>
              <li>
                <a
                  href="https://www.linkedin.com/company/authority-layer/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors duration-200"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="w-3.5 h-3.5 transition-colors duration-200 group-hover:text-primary"
                    aria-hidden="true"
                  >
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                  <span>Follow us on LinkedIn</span>
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="text-sm font-heading font-semibold text-foreground mb-4">Legal</h3>
            <ul className="space-y-2">
              <li><Link to="/legal/privacy" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Privacy Policy</Link></li>
              <li><Link to="/legal/terms" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Terms of Service</Link></li>
              <li><Link to="/refund-policy" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Refund Policy</Link></li>
              <li>
                <Button variant="link"
                  type="button"
                  onClick={openConsentBanner}
                  className="h-auto p-0 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {es ? "Preferencias de cookies" : "Cookie Preferences"}
                </Button>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="text-sm font-heading font-semibold text-foreground mb-4">Support</h3>
            <ul className="space-y-2">
              <li><Link to="/company/contact" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Contact</Link></li>
              <li><a href={`mailto:${SUPPORT_EMAIL}`} className="text-sm text-muted-foreground hover:text-foreground transition-colors">{SUPPORT_EMAIL}</a></li>
              <li><Link to="/auth/sign-in" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Sign In</Link></li>
            </ul>
          </div>
        </div>
        <div className="mt-10 pt-6 border-t border-border">
          <div className="flex flex-col items-center text-center gap-1">
            <p className="text-xs font-medium text-foreground">AuthorityLayer</p>
            <p className="text-xs text-muted-foreground mt-2">
              © {new Date().getFullYear()} • All rights reserved
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
